import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Alert, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSave, 
  faTrash, 
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { userService } from '../../services/userService';
import { companyService } from '../../services/companyService';

const UserEdit = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'manager',
    companyId: '',
    status: 'active'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [userData, companiesData] = await Promise.all([
          userService.getUserById(userId),
          companyService.getAllCompanies()
        ]);
        
        setUser(userData);
        setCompanies(companiesData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          role: userData.role || 'manager',
          companyId: userData.companyId || '',
          status: userData.status || 'active'
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await userService.updateUser(userId, formData);
      setSuccess('User updated successfully!');
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/admin/users/${userId}`);
      }, 1500);
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete user "${user?.name}"? This action cannot be undone.`)) {
      // Handle delete logic
      navigate('/admin/users');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faUser} className="me-2" />
          {error}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/users')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Users
        </Button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          User not found
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/users')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Users
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate(`/admin/users/${userId}`)}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to User
          </Button>
          <div>
            <h2 className="mb-1">Edit User</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faUser} className="me-1" />
              {user.name}
            </p>
          </div>
        </div>
        <Button 
          variant="outline-danger" 
          onClick={handleDelete}
          disabled={user.role === 'admin'}
        >
          <FontAwesomeIcon icon={faTrash} className="me-2" />
          Delete User
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">User Information</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                      >
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company</Form.Label>
                      <Form.Select
                        name="companyId"
                        value={formData.companyId}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Company</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex gap-2">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline-secondary"
                    onClick={() => navigate(`/admin/users/${userId}`)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserEdit; 